import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Badge, Button, Card, Heading, HStack, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { BaseballCard } from '@src/components/sco/Advisors/cards/BaseballCard';
import { ADVISORS_MY_FOUNDERS_GRID_SIZE, ADVISORS_ORDER_BY_LAST_ACTIVITY } from '@src/components/sco/Advisors/constants';
import { MyFoundersLayout } from '@src/components/sco/Advisors/layout/MyFoundersLayout';
import { OpportunitiesAccessGate } from '@src/components/sco/Advisors/layout/OpportunitiesLayout';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { engagementToFounderBaseballCard, founderDisplayName, founderUserIdOf, isActiveFounderEngagement } from '@src/components/sco/Advisors/utils/mapFounderEngagements';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ADVISOR_ENGAGEMENT_STATUS_ACTIVE } from '@src/constants/advisor-engagement-statuses';
import { ADVISOR_FOUNDER_ENGAGEMENTS } from '@src/gql/queries/AdvisorFounderEngagements';
import { ScoAdvisorEngagement } from '@src/graphql-types';
import { useMemo } from 'react';
type FounderEngagementsResult = {
  advisorEngagements?: {
    items?: Array<ScoAdvisorEngagement | null> | null;
    pagination?: {
      itemCount?: number | null;
      pageCount?: number | null;
      hasNextPage?: boolean | null;
    } | null;
  } | null;
};

/** Supply side only: same gate as Opportunities, so a non-advisor never sees an empty roster (#25). */
export const MyFoundersSection = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <OpportunitiesAccessGate><MyFoundersGrid /></OpportunitiesAccessGate>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const MyFoundersGrid = () => {
  const $ = _c(25);
  const paths = useAdvisorsPaths();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      variables: {
        statusKeys: [ADVISOR_ENGAGEMENT_STATUS_ACTIVE],
        page: 1,
        pageSize: ADVISORS_MY_FOUNDERS_GRID_SIZE,
        orderBy: ADVISORS_ORDER_BY_LAST_ACTIVITY
      },
      fetchPolicy: "cache-and-network"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const gridQuery = useQuery(ADVISOR_FOUNDER_ENGAGEMENTS, t0);
  gridQuery.data?.advisorEngagements?.items;
  let t1;
  if ($[1] !== gridQuery.data?.advisorEngagements?.items) {
    t1 = gridQuery.data?.advisorEngagements?.items || [];
    $[1] = gridQuery.data?.advisorEngagements?.items;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = t1.filter(isActiveFounderEngagement);
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const gridEngagements = t2 as ScoAdvisorEngagement[];
  let t3;
  if ($[5] !== gridEngagements || $[6] !== paths.base) {
    let t4;
    if ($[8] !== paths.base) {
      t4 = engagement => ({
        key: engagement.advisorEngagementId || engagement.id || founderUserIdOf(engagement) || founderDisplayName(engagement),
        card: engagementToFounderBaseballCard(engagement, {
          base: paths.base
        })
      });
      $[8] = paths.base;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    t3 = gridEngagements.map(t4);
    $[5] = gridEngagements;
    $[6] = paths.base;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const cards = t3;
  const itemCount = gridQuery.data?.advisorEngagements?.pagination?.itemCount ?? gridEngagements.length;
  const hasMoreFounders = itemCount > cards.length || !!gridQuery.data?.advisorEngagements?.pagination?.hasNextPage;
  const loading = gridQuery.loading && !gridQuery.data;
  const error = gridQuery.error;
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Text color="colorPalette.fg/60" textStyle="labelXs">Active relationships</Text>;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== itemCount) {
    t5 = itemCount > 0 ? <Badge variant="countGeneric">{itemCount} {itemCount === 1 ? "founder" : "founders"}</Badge> : null;
    $[11] = itemCount;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = <HStack align="center" justify="space-between">{t4}{t5}</HStack>;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== cards || $[16] !== hasMoreFounders || $[17] !== paths.myFoundersAll || $[18] !== paths.opportunities) {
    t7 = cards.length === 0 ? <VStack align="center" borderColor="colorPalette.border" borderRadius="xl" borderStyle="dashed" borderWidth="1px" gap={3} justify="center" padding={4} paddingY={10}><Text color="colorPalette.fg/60" textAlign="center">No active founder relationships yet. Accept board invites or apply to open seats from Opportunities.</Text><Button asChild={true} size="sm" variant="cta"><NextLink href={paths.opportunities}>Go to Opportunities</NextLink></Button></VStack> : <SimpleGrid gap={5} minChildWidth="14rem">{cards.map(_temp)}{hasMoreFounders ? <Card.Root variant="outline"><Card.Body><HStack align="center" gap={4} justify="space-between" wrap="wrap"><VStack align="stretch" gap={1}><Heading size="md">Looking for someone?</Heading><Text color="colorPalette.fg/70" fontSize="sm">Browse your full founder roster, or explore new board opportunities.</Text></VStack><HStack gap={2} wrap="wrap"><Button asChild={true} variant="outline"><a href={paths.myFoundersAll}>View all</a></Button><Button asChild={true} variant="cta"><a href={paths.opportunities}>Opportunities</a></Button></HStack></HStack></Card.Body></Card.Root> : <Card.Root asChild={true} variant="outline"><a href={paths.opportunities}><Card.Body><VStack align="center" gap={4} height="full" justify="center"><VStack align="stretch" gap={1} textAlign="center"><Heading size="md">Find more founders</Heading><Text color="colorPalette.fg/70" fontSize="sm">Apply to open seats and grow the list of founders you support.</Text></VStack><Button pointerEvents="none" variant="cta">Opportunities</Button></VStack></Card.Body></a></Card.Root>}</SimpleGrid>;
    $[15] = cards;
    $[16] = hasMoreFounders;
    $[17] = paths.myFoundersAll;
    $[18] = paths.opportunities;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== error || $[21] !== loading || $[22] !== t6 || $[23] !== t7) {
    t8 = <MyFoundersLayout activeKey="active"><AdvisorsQueryState error={error} loading={loading}>{t6}{t7}</AdvisorsQueryState></MyFoundersLayout>;
    $[20] = error;
    $[21] = loading;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
};
function _temp(t0) {
  const {
    key,
    card
  } = t0;
  return <BaseballCard key={key} card={card} />;
}